import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, Switch } from "@mui/material";
import { Close, DeleteOutline, Edit } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";
import { useAuth } from "../auth/authContext";
import { findNavItemByPath } from "../navigation/navDataUtils";

const ROWS_PER_PAGE = 10;

const FuelYourRaceManagement = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState({ name: "", direction: "asc" });
  const [write, setWrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteState, setDeleteState] = useState({ status: false, id: "" });
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    const matchingNavItem = findNavItemByPath(navData, location.pathname);
    setWrite(Boolean(matchingNavItem?.write));
  }, [location.pathname, navData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchPlans = useCallback(() => {
    setLoading(true);
    Axios.get(`${BACKEND_URL}fuel-your-race`, {
      params: {
        page: page + 1,
        limit: ROWS_PER_PAGE,
        search: search || undefined,
        sortField: sortOrder.name || undefined,
        sortOrder: sortOrder.direction || undefined,
      },
    })
      .then((res) => {
        setRows(res?.data?.data?.docs || []);
        setTotalCount(res?.data?.data?.totalDocs || 0);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, page, search, sortOrder]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPlans();
  }, [fetchPlans]);

  const deletePlan = () => {
    setLoading(true);
    Axios.delete(`${BACKEND_URL}fuel-your-race/${deleteState.id}`)
      .then((res) => {
        toast.success(res?.data?.message || "Deleted successfully");
        setDeleteState({ status: false, id: "" });
        fetchPlans();
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const toggleStatus = (id, currentActive) => {
    setLoading(true);
    const request = currentActive
      ? Axios.patch(`${BACKEND_URL}fuel-your-race/${id}/deactivate`)
      : Axios.patch(`${BACKEND_URL}fuel-your-race/${id}/activate`);

    request
      .then((res) => {
        toast.success(res?.data?.message || "Status updated");
        fetchPlans();
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const columns = [
    {
      name: "profileimg",
      label: "Profile Image",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => {
          const img = rows[i]?.profileimg;
          return img ? (
            <img
              src={img}
              alt=""
              style={{ width: 50, height: 50, objectFit: "cover", borderRadius: "50%", backgroundColor: "#f7f7f7", border: "1px solid #ddd" }}
            />
          ) : (
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "#eee", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", color: "#666" }}>
              N/A
            </Box>
          );
        },
      },
    },
    {
      name: "planName",
      label: "Plan Name",
      options: {
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => rows[i]?.planName || "-",
      },
    },
    {
      name: "type",
      label: "Type",
      options: {
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => (rows[i]?.type ? <Chip label={rows[i]?.type} size="small" /> : "-"),
      },
    },
    {
      name: "subtitle",
      label: "Subtitle",
      options: {
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => rows[i]?.subtitle || "-",
      },
    },
    {
      name: "price",
      label: "Price",
      options: {
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => (rows[i]?.price !== undefined ? `$${rows[i]?.price}` : "-"),
      },
    },
    {
      name: "discountPercentage",
      label: "% Off",
      options: {
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => (rows[i]?.discountPercentage ? `${rows[i]?.discountPercentage}%` : "-"),
      },
    },
    {
      name: "couponCode",
      label: "Coupon Code",
      options: {
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) =>
          rows[i]?.couponCode ? (
            <Chip label={rows[i]?.couponCode} variant="outlined" sx={{ fontSize: "0.9rem", fontWeight: 600, height: 32, px: 0.5 }} />
          ) : (
            "-"
          ),
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => (rows[i]?.is_active ? "Active" : "Inactive"),
      },
    },
    write && {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap" } }),
        customBodyRenderLite: (i) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={Boolean(rows[i]?.is_active)}
              disabled={loading}
              onChange={() => toggleStatus(rows[i]?._id, rows[i]?.is_active)}
              size="small"
            />
            <Edit sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }} onClick={() => navigate(`/fuel-your-race-management/edit/${rows[i]?._id}`)} />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() => setDeleteState({ status: true, id: rows[i]?._id })}
            />
          </Box>
        ),
      },
    },
  ].filter(Boolean);

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Fuel Your Race Management</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Fuel Your Race Management</h2>
        {write && (
          <Button variant="contained" onClick={() => navigate("/fuel-your-race-management/add")}>
            Create Plan
          </Button>
        )}
      </div>

      <MUIDataTable
        title="Fuel Your Race Plans"
        data={rows}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          viewColumns: false,
          download: false,
          serverSide: true,
          search: true,
          searchText: searchInput,
          count: totalCount,
          page,
          sortOrder,
          rowsPerPage: ROWS_PER_PAGE,
          rowsPerPageOptions: [],
          textLabels: { body: { noMatch: loading ? "Loading..." : "No records found" } },
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
            } else if (action === "sort") {
              setSortOrder(tableState.sortOrder);
              setPage(0);
            }
          },
          onSearchChange: (searchQuery) => {
            setSearchInput(searchQuery || "");
          },
          onSearchClose: () => {
            setSearchInput("");
          },
        }}
      />

      <Dialog open={deleteState.status} onClose={() => setDeleteState({ status: false, id: "" })} fullWidth>
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete Record</h3>
          <IconButton onClick={() => setDeleteState({ status: false, id: "" })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setDeleteState({ status: false, id: "" })}>
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" disabled={loading} onClick={deletePlan}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FuelYourRaceManagement;